import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn, projectStatus } from "@/lib/utils";
import { setProjectData } from "@/redux/slices";
import { routes } from "@/routes/utils";
import { useGetProjectList } from "@/services";
import { Edit, MoreVertical } from "lucide-react";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

const useProjectOverview = () => {
  const dispatch = useDispatch();
  const [globalFilter, setGlobalFilter] = useState("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [deletedRow] = useState<any>(null);
  const [showInActiveConfirmation, setShowInActiveConfirmation] =
    useState<boolean>(false);

  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const { data: projectList, isLoading: isProjectListLoading } =
    useGetProjectList();

  // Define Table Columns
  const columns = [
    {
      accessorKey: "projectId",
      header: "ID",
      accessorFn: (row: any) => String(row?.projectId || ""),
      size: 100,
    },
    {
      accessorKey: "name",
      header: "Project Name",
      cell: ({ row }: any) => {
        return row.original.name ? (
          <span
            className="text-secondary border-b-1 border-secondary group-hover:border-secondary cursor-pointer font-[500]"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dispatch(setProjectData(null));
              navigate(`${row.original.projectId}/view/${routes.projectInfo}`);
            }}
          >
            {String(row.original.name)}
          </span>
        ) : (
          <div className="px-3">{""}</div>
        );
      },
      size: 200,
    },
    // {
    //   accessorKey: "type",
    //   header: "Project Type",
    //   accessorFn: (row: any) => String(row?.type || ""),
    //   size: 100,
    // },
    {
      accessorKey: "estimatedStartDate",
      header: "Estimated Start Date",
      accessorFn: (row: any) =>
        row?.estimatedStartDate
          ? moment(row?.estimatedStartDate).format("MM-DD-YYYY")
          : "",
      size: 100,
    },
    {
      accessorKey: "estimatedEndDate",
      header: "Estimated End Date",
      accessorFn: (row: any) =>
        row?.estimatedEndDate
          ? moment(row?.estimatedEndDate).format("MM-DD-YYYY")
          : "",
      size: 100,
    },
    {
      accessorKey: "location",
      header: "Project Location",
      accessorFn: (row: any) => String(row?.location || ""),
      size: 100,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (row: any) => (
        <span
          className={cn(
            "font-[600]",
            projectStatus[row?.row?.original?.status?.toLowerCase()]?.className
          )}
        >
          {projectStatus[row?.row?.original?.status?.toLowerCase()]?.label ||
            ""}
        </span>
      ),
      size: 100,
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <MoreVertical className="w-5 h-5 cursor-pointer" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white border-0 shadow-md"
          >
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dispatch(setProjectData(null));
                navigate(
                  `${row.row?.original?.projectId}/edit/${routes.projectInfo}`
                );
              }}
            >
              <Edit className="w-4 h-4" />
              Edit
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const onClose = () => {
    setOpenDialog(false);
  };

  const onAdd = () => {
    dispatch(setProjectData(null));
    navigate(`${routes.add}/${routes.projectInfo}`);
  };

  return {
    projectList,
    isProjectListLoading,
    onAdd,
    globalFilter,
    setGlobalFilter,
    columns,
    onClose,
    openDialog,
    deletedRow,
    isMobile,
    showInActiveConfirmation,
    setShowInActiveConfirmation,
  };
};

export default useProjectOverview;
